import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import CopyButton from "./CopyButton";
import { AgendaChip, city, dateOnly, day, daysUntil, eventSentence, pad2, range, stamp } from "./bookingFormat";

/*
 * TAB 1: BOOKINGS. Upcoming · Past · Cancelled, a search, and the row opens
 * its details BESIDE the list (never a navigation, per the CRM's row rule).
 * On Cancelled, "Check back in" leads the view; on Upcoming it is one line.
 */

const VIEWS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "cancelled", label: "Cancelled" },
];

function NoteIcon() {
  return (
    <svg className="crm-bknote" viewBox="0 0 24 24" aria-label="Has a note" role="img">
      <path d="M4 4h16v12H8l-4 4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIns({ items, full, onSeeAll, linkFor, zone, agendas }) {
  if (!items.length) return null;
  if (!full) {
    return (
      <div className="crm-bkcheckline">
        <b>Check back in</b>
        <span>
          {items
            .slice(0, 2)
            .map((c) => `${c.name}, ${dateOnly(c.check_in_at).replace(/^\w+, /, "")}`)
            .join(" · ")}
        </span>
        <button type="button" className="crm-linkact" onClick={onSeeAll}>
          See all
        </button>
      </div>
    );
  }
  return (
    <section className="crm-card crm-bkcheckins">
      <div className="crm-bkcheckins__head">
        <b>Check back in</b>
        <span className="crm-hint">Leads who asked you to get in touch again. Soonest first.</span>
      </div>
      {items.map((c) => {
        const inDays = daysUntil(c.check_in_at);
        return (
          <div key={c.id} className="crm-bkcheckins__row">
            <div>
              <b>{dateOnly(c.check_in_at)}</b>
              <span className="crm-hint">{inDays > 0 ? `in ${inDays} days` : inDays === 0 ? "today" : `${-inDays} days ago`}</span>
            </div>
            <div>
              <b>{c.name}</b>
              {c.company ? <span className="crm-hint"> · {c.company}</span> : null}
              <small className="crm-bkcheckins__why">
                {[
                  c.cancelled_at && daysUntil(c.check_in_at, new Date(c.cancelled_at)) > 20 ? "In a month" : "Try again in 2 weeks",
                  c.starts_at ? `cancelled ${day(c.starts_at, zone)}` : null,
                  c.intent && agendas && agendas[c.intent] ? agendas[c.intent].label : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </small>
            </div>
            {c.contact_id ? <LinkCopy contactId={c.contact_id} linkFor={linkFor} /> : <span />}
          </div>
        );
      })}
    </section>
  );
}

function LinkCopy({ contactId, linkFor }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    linkFor(contactId).then((r) => setUrl(r.url)).catch(() => setUrl(null));
  }, [contactId, linkFor]);
  return <CopyButton text={url} label="Copy booking link" />;
}

function Detail({ id, zone, agendas, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    setDetail(null);
    api(`/booking/bookings/${id}`).then(setDetail).catch(() => setDetail({ error: true }));
  }, [id]);
  if (!detail) return <aside className="crm-card crm-bkpanel"><p className="crm-hint">Loading…</p></aside>;
  if (detail.error) return <aside className="crm-card crm-bkpanel"><p className="crm-hint">Couldn't load that booking.</p></aside>;

  const b = detail.booking;
  const cancelled = b.status === "cancelled";
  const past = !cancelled && new Date(b.end) < new Date();
  const agenda = b.intent && agendas ? agendas[b.intent] : null;
  const first = String(b.name || "").split(" ")[0];

  return (
    <aside className="crm-card crm-bkpanel">
      <div className="crm-bkpanel__head">
        <div>
          <h2>
            {b.name}{" "}
            <span className={`crm-pill ${cancelled ? "crm-pill--cancelled" : past ? "crm-pill--draft" : "crm-pill--scheduled"}`}>
              {cancelled ? "Cancelled" : past ? "Past" : "Booked"}
            </span>
          </h2>
          <p className="crm-hint">
            {[b.company, b.email, b.personal ? "Personal link" : "Anonymous"].filter(Boolean).join(" · ")}
          </p>
        </div>
        <button type="button" className="crm-peek__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="crm-bkpanel__sec">
        <div className={`crm-bkpanel__times${cancelled ? " is-struck" : ""}`}>
          <div>
            <span className="crm-bkmono">Your time · {city(zone)}</span>
            <b>{day(b.start, zone)}</b>
            <b>{range(b.start, b.end, zone)}</b>
          </div>
          <div>
            <span className="crm-bkmono">
              For {first} · {city(b.timezone)}
            </span>
            <b>{day(b.start, b.timezone)}</b>
            <b>{range(b.start, b.end, b.timezone)}</b>
          </div>
        </div>
        {cancelled ? (
          <p className="crm-bkpanel__cancel">
            Cancelled {b.cancelled_at ? stamp(b.cancelled_at, zone) : ""}.{" "}
            {b.check_in_at ? `Check back in: ${dateOnly(b.check_in_at)}.` : "No check-in."}
          </p>
        ) : b.meet_url ? (
          <div className="crm-bkmeet">
            <span className="crm-bkmono">Meet</span>
            <a href={b.meet_url} target="_blank" rel="noreferrer">
              {b.meet_url.replace(/^https?:\/\//, "")}
            </a>
            <CopyButton text={b.meet_url} />
          </div>
        ) : null}
      </div>

      {/* Only intro calls have an agenda; a friendly chat never offered one. */}
      {b.type && b.type !== "intro" ? null : (
      <div className="crm-bkpanel__sec">
        <div className="crm-bkpanel__row">
          <span className="crm-bkmono">Agenda</span>
          <AgendaChip intent={b.intent} />
        </div>
        {agenda ? (
          <>
            <b className="crm-bkpanel__agenda">{agenda.label}</b>
            <ol className="crm-bklines">
              {agenda.items.map((item) => (
                <li key={item.at}>
                  <span>{pad2(item.at)}</span>
                  {item.title}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <p className="crm-hint">Not chosen yet.</p>
        )}
      </div>
      )}

      <div className="crm-bkpanel__sec">
        <span className="crm-bkmono">Their note</span>
        {b.note ? <p className="crm-bkpanel__note">{b.note}</p> : <p className="crm-hint">No note.</p>}
        {b.contact_id ? (
          <Link className="crm-linkact" to={`/contacts/${b.contact_id}`}>
            Open the CRM record →
          </Link>
        ) : null}
      </div>

      <div className="crm-bkpanel__sec">
        <span className="crm-bkmono">History</span>
        <ul className="crm-bkhistory">
          {detail.events.map((e) => {
            const s = eventSentence(e, { zone, agendas, withWho: false });
            return (
              <li key={e.id} className={e.kind === "cancelled" ? "is-key" : ""}>
                <span>{stamp(e.created_at, zone)}</span>
                <span>{s.text}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}

export default function BookingsTab({ data }) {
  const zone = data.calendar.timezone;
  const agendas = data.settings.agendas;
  // A booking's meeting type by key; a removed type still reads as its key.
  const typeName = (key) => ((data.settings.types || []).find((t) => t.key === key) || { name: key }).name;
  const [view, setView] = useState("upcoming");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [open, setOpen] = useState(null);

  const linkFor = useCallback((contactId) => api(`/booking/link/${contactId}`), []);

  useEffect(() => {
    const handle = setTimeout(() => {
      api(`/booking/bookings?view=${view}&q=${encodeURIComponent(q)}`)
        .then(setList)
        .catch(() => setList({ bookings: [], counts: {} }));
    }, q ? 250 : 0);
    return () => clearTimeout(handle);
  }, [view, q]);

  useEffect(() => {
    api("/booking/check-ins").then(setCheckIns).catch(() => setCheckIns([]));
  }, []);

  const counts = (list && list.counts) || {};
  const rows = (list && list.bookings) || [];

  return (
    <div className={`crm-bkbookings${open ? " has-panel" : ""}`}>
      <div className="crm-bkbookings__main">
        <div className="crm-bktoolbar">
          <span className="crm-bkviews">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                className={view === v.key ? "sel" : ""}
                onClick={() => {
                  setView(v.key);
                  setOpen(null);
                }}
              >
                {v.label} <span>{counts[v.key] ?? ""}</span>
              </button>
            ))}
          </span>
          <input
            className="crm-input crm-bksearch"
            type="search"
            placeholder="Search name, email or company"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {view === "cancelled" ? <CheckIns items={checkIns} full linkFor={linkFor} zone={zone} agendas={agendas} /> : null}

        <div className="crm-card crm-bktable">
          <div className="crm-bktable__head">
            <span>{view === "cancelled" ? "Was booked for" : `When · your time`}</span>
            <span>Who</span>
            <span>Agenda</span>
            <span />
            <span>{view === "cancelled" ? "Check back" : "Link"}</span>
          </div>
          {!list ? (
            <p className="crm-hint crm-bktable__empty">Loading…</p>
          ) : !rows.length ? (
            <p className="crm-hint crm-bktable__empty">
              {q ? "Nothing matches that search." : view === "upcoming" ? "No calls booked yet." : "Nothing here yet."}
            </p>
          ) : (
            rows.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`crm-bktable__row${open === b.id ? " is-open" : ""}`}
                onClick={() => setOpen(open === b.id ? null : b.id)}
              >
                <span className={b.status === "cancelled" ? "is-struck" : ""}>
                  <b>{day(b.start, zone)}</b>
                  <small>{range(b.start, b.end, zone)}</small>
                </span>
                <span>
                  <b>{b.name}</b>
                  <small>{[b.company, b.email].filter(Boolean).join(" · ")}</small>
                </span>
                <span>
                  {b.type && b.type !== "intro" ? (
                    <span className="crm-bkchip crm-bkchip--type">{typeName(b.type)}</span>
                  ) : (
                    <AgendaChip intent={b.intent} />
                  )}
                </span>
                <span className="crm-bktable__flags">
                  {b.note ? <NoteIcon /> : null}
                  {b.reschedule_count ? <small>moved ×{b.reschedule_count}</small> : null}
                </span>
                <span>
                  {view === "cancelled"
                    ? b.check_in_at
                      ? `Check back ${dateOnly(b.check_in_at).replace(/^\w+, /, "")}`
                      : "No check-in"
                    : b.personal
                    ? "Personal link"
                    : "Anonymous"}
                </span>
              </button>
            ))
          )}
        </div>

        {view === "upcoming" ? <CheckIns items={checkIns} onSeeAll={() => setView("cancelled")} linkFor={linkFor} zone={zone} agendas={agendas} /> : null}
      </div>

      {open ? <Detail id={open} zone={zone} agendas={agendas} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
